import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, MapPin, Building2, ArrowLeft, User, Loader2, Handshake, ArrowUpRight,
  Globe, Package, Compass, Target,
} from "lucide-react";
import Seo from "../components/Seo";
import AppLayout from "../components/AppLayout";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { INDUSTRIES, MEMBER_TYPES } from "../data/hub";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = "__all__";
const INTEREST_TYPES = [
  "Business Partnership", "Cross-Selling", "Need Their Services", "Offer My Services",
  "Investment", "Mentorship", "Collaboration", "Networking", "Other",
];

// Quick-filter chips (must match existing filter values 1:1)
const BIZ_QUICK_CHIPS = [
  { label: "Technology", type: "industry", value: "Technology" },
  { label: "Marketing", type: "industry", value: "Marketing" },
  { label: "Consulting", type: "industry", value: "Consulting" },
  { label: "Finance", type: "industry", value: "Finance" },
  { label: "UAE", type: "country", value: "United Arab Emirates" },
  { label: "India", type: "country", value: "India" },
];
const PRO_QUICK_CHIPS = [
  { label: "Founders", type: "member_type", value: "Founder / Co-Founder" },
  { label: "Consultants", type: "member_type", value: "Consultant" },
  { label: "Freelancers", type: "member_type", value: "Freelancer" },
  { label: "Mentors", type: "member_type", value: "Mentor / Advisor" },
  { label: "Investors", type: "member_type", value: "Investor" },
  { label: "Growth Partners", type: "member_type", value: "Growth Partner" },
];

function DemoBadge() {
  return (
    <span className="rounded-full bg-[color:var(--brand-teal)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]">
      Sample
    </span>
  );
}

function ChipList({ items, tone = "soft", limit = 3 }) {
  if (!items || items.length === 0) return null;
  const visible = items.slice(0, limit);
  const overflow = items.length - visible.length;
  const cls = {
    soft: "bg-[color:var(--soft)] text-[color:var(--ink)]",
    green: "bg-[color:var(--brand-green)]/10 text-[color:var(--brand-green)]",
    orange: "bg-[color:var(--brand-orange)]/10 text-[color:var(--brand-orange)]",
  }[tone];
  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((x) => (
        <span key={x} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{x}</span>
      ))}
      {overflow > 0 && <span className="rounded-full bg-transparent px-2 py-0.5 text-[11px] font-medium text-[color:var(--ink-soft)]">+{overflow}</span>}
    </div>
  );
}

function QuickChips({ chips, filters, onChipClick, testIdPrefix }) {
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-quick-chips`}>
      {chips.map((c) => {
        const active = filters[c.type] === c.value;
        return (
          <button
            key={c.label}
            type="button"
            onClick={() => onChipClick(c)}
            data-testid={`${testIdPrefix}-chip-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              active
                ? "border-[color:var(--ink)] bg-[color:var(--ink)] text-white"
                : "border-[color:var(--hairline)] bg-[color:var(--surface)] text-[color:var(--ink-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"
            }`}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

function ExpressInterestButton({ target_user_id, target_business_id, size = "md" }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("Business Partnership");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/hub/interests`, { target_user_id, target_business_id, interest_type: type, message }, { withCredentials: true });
      toast.success("Interest sent.");
      setOpen(false);
      setMessage("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send interest."); }
    finally { setSending(false); }
  };
  const cls = size === "lg"
    ? "inline-flex items-center gap-2 rounded-full bg-[color:var(--invert-bg)] px-6 py-3 text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform"
    : "inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform";
  return (<>
    <button onClick={() => setOpen(true)} data-testid="express-interest-btn" className={cls}>
      <Handshake className="h-4 w-4" /> Express Interest
    </button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="interest-dialog">
        <DialogHeader><DialogTitle>Express interest</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Reason</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="interest-type" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{INTEREST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Short message (optional)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="min-h-24 rounded-xl" data-testid="interest-message" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => setOpen(false)} className="rounded-full border border-[color:var(--hairline)] px-5 py-2.5 text-sm font-semibold">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="interest-send" className="rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white">{sending ? "Sending…" : "Send"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}

function DirectoryHeader({ eyebrow, title, sub, icon: Icon }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="max-w-2xl">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--brand-teal)]">
          <Icon className="h-3.5 w-3.5" /> {eyebrow}
        </p>
        <h1 className="font-display mt-2 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">{title}</h1>
        <p className="mt-2 text-sm text-[color:var(--ink-soft)] sm:text-base">{sub}</p>
      </div>
    </div>
  );
}

// ---------------- Businesses ----------------

export function BusinessesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [country, setCountry] = useState("");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (industry) params.industry = industry;
    if (country) params.country = country;
    try {
      const { data } = await axios.get(`${API}/hub/discover/businesses`, { params, withCredentials: true });
      setItems(data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [industry, country]);

  const countries = useMemo(() => [...new Set(items.map((i) => i.country).filter(Boolean))].sort(), [items]);

  const onChip = (c) => {
    if (c.type === "industry") setIndustry(industry === c.value ? "" : c.value);
    if (c.type === "country") setCountry(country === c.value ? "" : c.value);
  };
  const clearAll = () => { setQ(""); setIndustry(""); setCountry(""); };
  const hasFilters = q || industry || country;

  return (
    <AppLayout>
      <Seo title="Discover Businesses" description="Find startups, businesses and service providers to collaborate with, buy from, partner with and grow alongside." path="/app/businesses" />
      <div className="container-hub py-8 lg:py-10" data-testid="businesses-page">
        <DirectoryHeader
          eyebrow="Discover"
          title="Discover Businesses"
          sub="Find startups, businesses and service providers to collaborate with, buy from, partner with and grow alongside."
          icon={Compass}
        />

        <QuickChips
          chips={BIZ_QUICK_CHIPS}
          filters={{ industry, country }}
          onChipClick={onChip}
          testIdPrefix="biz"
        />

        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-6 flex flex-wrap items-end gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--ink-soft)]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, industry, services" className="h-11 rounded-xl pl-9" data-testid="biz-search" />
          </div>
          <Select value={industry || ALL} onValueChange={(v) => setIndustry(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="biz-filter-industry" className="h-11 w-[170px] rounded-xl"><SelectValue placeholder="Industry" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All industries</SelectItem>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={country || ALL} onValueChange={(v) => setCountry(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="biz-filter-country" className="h-11 w-[170px] rounded-xl"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All countries</SelectItem>{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          {hasFilters && (
            <button type="button" onClick={clearAll} data-testid="biz-clear-filters" className="rounded-full border border-[color:var(--hairline)] px-4 py-2.5 text-xs font-semibold text-[color:var(--ink-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]">
              Clear filters
            </button>
          )}
        </form>

        {loading ? (
          <div className="mt-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div>
        ) : items.length === 0 ? (
          <p className="mt-16 text-center text-[color:var(--ink-soft)]">No businesses match your filters.</p>
        ) : (
          <>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]" data-testid="biz-result-count">
              {items.length} business{items.length === 1 ? "" : "es"}
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((b) => <BusinessCard key={b.id} b={b} />)}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

function BusinessCard({ b }) {
  const services = (b.products_services || "").split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  return (
    <Link
      to={`/app/businesses/${b.id}`}
      data-testid={`biz-card-${b.id}`}
      className="hub-card group flex flex-col rounded-2xl p-6 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(10,10,10,0.06)]"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[color:var(--soft)]">
          {b.logo ? <img src={b.logo} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-5 w-5 text-[color:var(--ink-soft)]" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-display truncate text-base font-semibold text-[color:var(--ink)]">{b.name}</p>
            {b.is_demo && <DemoBadge />}
          </div>
          <p className="mt-0.5 text-xs text-[color:var(--ink-soft)]">
            <span className="font-medium text-[color:var(--ink)]">{b.industry}</span>
            {b.stage && <span className="text-[color:var(--ink-soft)]"> · {b.stage}</span>}
          </p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-[color:var(--ink-soft)]">
            <MapPin className="h-3 w-3" />{[b.city, b.country].filter(Boolean).join(", ") || "—"}
          </p>
        </div>
      </div>

      {b.description && (
        <p className="mt-4 text-sm text-[color:var(--ink-soft)] line-clamp-2">{b.description}</p>
      )}

      {services.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">Services</p>
          <div className="mt-1.5"><ChipList items={services} limit={3} tone="soft" /></div>
        </div>
      )}

      {(b.markets || []).length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">Markets</p>
          <div className="mt-1.5"><ChipList items={b.markets} limit={4} tone="soft" /></div>
        </div>
      )}

      <div className="mt-auto flex items-center justify-between border-t border-[color:var(--hairline)] pt-4 text-xs text-[color:var(--ink-soft)]">
        <span className="inline-flex items-center gap-1"><Package className="h-3 w-3" />View business</span>
        <ArrowUpRight className="h-4 w-4 text-[color:var(--ink)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </Link>
  );
}

export function BusinessDetailPage() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/hub/discover/businesses/${id}`, { withCredentials: true }); setB(data); }
      catch { toast.error("Business not found."); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <AppLayout><div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div></AppLayout>;
  if (!b) return <AppLayout><div className="container-hub py-10">Not found.</div></AppLayout>;

  const owner = b.owner || {};
  return (
    <AppLayout>
      <Seo title={b.name} description={(b.description || "").slice(0, 160)} path={`/app/businesses/${b.id}`} image={b.logo || undefined} />
      <div className="container-hub py-8 lg:py-10" data-testid="business-detail-page">
        <Link to="/app/businesses" className="mb-3 inline-flex items-center gap-1 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">
          <ArrowLeft className="h-3.5 w-3.5" /> Businesses
        </Link>

        <div className="hub-card rounded-3xl p-8 lg:p-10">
          <div className="brand-bar mb-6 h-1 w-16 rounded-full" />

          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-[color:var(--soft)]">
                {b.logo ? <img src={b.logo} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-6 w-6 text-[color:var(--ink-soft)]" />}
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-display text-2xl font-semibold text-[color:var(--ink)] sm:text-3xl">{b.name}</h1>
                  {b.is_demo && <DemoBadge />}
                </div>
                <p className="mt-1 text-sm text-[color:var(--ink-soft)]">
                  {[b.industry, b.stage, [b.city, b.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                </p>
                {b.website && (
                  <a href={b.website} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">
                    <Globe className="h-3 w-3" /> {b.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </a>
                )}
              </div>
            </div>
            <ExpressInterestButton target_business_id={b.id} target_user_id={owner.user_id} size="lg" />
          </div>

          {/* Value prop first — Offers + Looking For */}
          {(owner.offers?.length || owner.looking_for?.length) > 0 && (
            <section className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="biz-detail-value-prop">
              <div className="rounded-2xl border border-[color:var(--brand-green)]/30 bg-[color:var(--brand-green)]/5 p-5">
                <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-green)]">
                  <Package className="h-3.5 w-3.5" /> What we offer
                </p>
                <div className="mt-3"><ChipList items={owner.offers || []} limit={20} tone="green" /></div>
              </div>
              <div className="rounded-2xl border border-[color:var(--brand-orange)]/30 bg-[color:var(--brand-orange)]/5 p-5">
                <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-orange)]">
                  <Target className="h-3.5 w-3.5" /> What we're looking for
                </p>
                <div className="mt-3"><ChipList items={owner.looking_for || []} limit={20} tone="orange" /></div>
                {owner.looking_for_details && (
                  <p className="mt-3 text-xs text-[color:var(--ink-soft)]">{owner.looking_for_details}</p>
                )}
              </div>
            </section>
          )}

          {b.description && (
            <section className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">About</p>
              <p className="mt-2 whitespace-pre-line text-[color:var(--ink)]">{b.description}</p>
            </section>
          )}

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {b.products_services && (
              <div className="rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Products & services</p>
                <p className="mt-2 text-[color:var(--ink)]">{b.products_services}</p>
              </div>
            )}
            {(b.markets || []).length > 0 && (
              <div className="rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Markets served</p>
                <div className="mt-3"><ChipList items={b.markets} limit={20} tone="soft" /></div>
              </div>
            )}
          </div>

          {owner.user_id && (
            <div className="mt-8 border-t border-[color:var(--hairline)] pt-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Connected owner</p>
              <Link to={`/app/professionals/${owner.user_id}`} data-testid="biz-owner-link"
                className="mt-3 flex items-center gap-4 rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4 transition-colors hover:border-[color:var(--ink)]">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[color:var(--soft)]">
                  {owner.profile_photo ? <img src={owner.profile_photo} alt="" className="h-full w-full object-cover" /> : <User className="h-5 w-5 text-[color:var(--ink-soft)]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[color:var(--ink)]">{owner.full_name}</p>
                  <p className="truncate text-xs text-[color:var(--ink-soft)]">{owner.professional_headline}</p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-[color:var(--ink)]" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// ---------------- Professionals ----------------

export function ProfessionalsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [mtype, setMtype] = useState("");
  const [industry, setIndustry] = useState("");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (mtype) params.member_type = mtype;
    if (industry) params.industry = industry;
    try {
      const { data } = await axios.get(`${API}/hub/discover/professionals`, { params, withCredentials: true });
      setItems(data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mtype, industry]);

  const onChip = (c) => {
    if (c.type === "member_type") setMtype(mtype === c.value ? "" : c.value);
    if (c.type === "industry") setIndustry(industry === c.value ? "" : c.value);
  };
  const clearAll = () => { setQ(""); setMtype(""); setIndustry(""); };
  const hasFilters = q || mtype || industry;

  return (
    <AppLayout>
      <Seo title="Discover Professionals" description="Find founders, freelancers, consultants, mentors, investors and professionals across the Hub." path="/app/professionals" />
      <div className="container-hub py-8 lg:py-10" data-testid="professionals-page">
        <DirectoryHeader
          eyebrow="Discover"
          title="Discover Professionals"
          sub="Find founders, freelancers, consultants, mentors, investors and professionals across the Hub."
          icon={Compass}
        />

        <QuickChips
          chips={PRO_QUICK_CHIPS}
          filters={{ member_type: mtype, industry }}
          onChipClick={onChip}
          testIdPrefix="pro"
        />

        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-6 flex flex-wrap items-end gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--ink-soft)]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, headline, expertise" className="h-11 rounded-xl pl-9" data-testid="pro-search" />
          </div>
          <Select value={mtype || ALL} onValueChange={(v) => setMtype(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="pro-filter-type" className="h-11 w-[200px] rounded-xl"><SelectValue placeholder="Member type" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All types</SelectItem>{MEMBER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={industry || ALL} onValueChange={(v) => setIndustry(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="pro-filter-industry" className="h-11 w-[170px] rounded-xl"><SelectValue placeholder="Industry" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All industries</SelectItem>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
          {hasFilters && (
            <button type="button" onClick={clearAll} data-testid="pro-clear-filters" className="rounded-full border border-[color:var(--hairline)] px-4 py-2.5 text-xs font-semibold text-[color:var(--ink-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]">
              Clear filters
            </button>
          )}
        </form>

        {loading ? (
          <div className="mt-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div>
        ) : items.length === 0 ? (
          <p className="mt-16 text-center text-[color:var(--ink-soft)]">No professionals match your filters.</p>
        ) : (
          <>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]" data-testid="pro-result-count">
              {items.length} professional{items.length === 1 ? "" : "s"}
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((p) => <ProfessionalCard key={p.user_id} p={p} />)}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

function ProfessionalCard({ p }) {
  const pi = p.professional_info || {};
  const expertise = pi.expertise || [];
  const services = pi.services || [];
  return (
    <Link
      to={`/app/professionals/${p.user_id}`}
      data-testid={`pro-card-${p.user_id}`}
      className="hub-card group flex flex-col rounded-2xl p-6 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(10,10,10,0.06)]"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[color:var(--soft)]">
          {p.profile_photo ? <img src={p.profile_photo} alt="" className="h-full w-full object-cover" /> : <User className="h-6 w-6 text-[color:var(--ink-soft)]" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate font-display text-base font-semibold text-[color:var(--ink)]">{p.full_name}</p>
            {p.is_demo && <DemoBadge />}
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]">{p.member_type}</p>
          <p className="mt-1 text-sm text-[color:var(--ink)] line-clamp-2">{p.professional_headline}</p>
          <p className="mt-1 flex items-center gap-1 text-xs text-[color:var(--ink-soft)]">
            <MapPin className="h-3 w-3" />{[p.city, p.country].filter(Boolean).join(", ") || "—"}
          </p>
        </div>
      </div>

      {(expertise.length > 0 || services.length > 0) && (
        <div className="mt-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">
            {expertise.length ? "Expertise" : "Services"}
          </p>
          <div className="mt-1.5"><ChipList items={expertise.length ? expertise : services} limit={3} tone="soft" /></div>
        </div>
      )}

      {(p.offers?.length || p.looking_for?.length) > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {p.offers?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-green)]">Offers</p>
              <div className="mt-1.5"><ChipList items={p.offers} limit={2} tone="green" /></div>
            </div>
          )}
          {p.looking_for?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-orange)]">Looking for</p>
              <div className="mt-1.5"><ChipList items={p.looking_for} limit={2} tone="orange" /></div>
            </div>
          )}
        </div>
      )}

      <div className="mt-auto flex items-center justify-between border-t border-[color:var(--hairline)] pt-4 text-xs text-[color:var(--ink-soft)]">
        <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />View profile</span>
        <ArrowUpRight className="h-4 w-4 text-[color:var(--ink)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </Link>
  );
}

export function ProfessionalDetailPage() {
  const { user_id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/hub/discover/professionals/${user_id}`, { withCredentials: true }); setP(data); }
      catch { toast.error("Professional not found."); }
      finally { setLoading(false); }
    })();
  }, [user_id]);

  if (loading) return <AppLayout><div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div></AppLayout>;
  if (!p) return <AppLayout><div className="container-hub py-10">Not found.</div></AppLayout>;
  const pi = p.professional_info || {};
  return (
    <AppLayout>
      <Seo title={p.full_name} description={p.professional_headline} path={`/app/professionals/${p.user_id}`} image={p.profile_photo || undefined} />
      <div className="container-hub py-8 lg:py-10" data-testid="professional-detail-page">
        <Link to="/app/professionals" className="mb-3 inline-flex items-center gap-1 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">
          <ArrowLeft className="h-3.5 w-3.5" /> Professionals
        </Link>

        <div className="hub-card rounded-3xl p-8 lg:p-10">
          <div className="brand-bar mb-6 h-1 w-16 rounded-full" />
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[color:var(--soft)]">
                {p.profile_photo ? <img src={p.profile_photo} alt="" className="h-full w-full object-cover" /> : <User className="h-6 w-6 text-[color:var(--ink-soft)]" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-display text-2xl font-semibold sm:text-3xl">{p.full_name}</h1>
                  {p.is_demo && <DemoBadge />}
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]">{p.member_type}</p>
                {p.professional_headline && <p className="mt-1 max-w-xl text-[color:var(--ink)]">{p.professional_headline}</p>}
                <p className="mt-1 flex items-center gap-1 text-xs text-[color:var(--ink-soft)]">
                  <MapPin className="h-3 w-3" />{[p.city, p.country].filter(Boolean).join(", ") || "—"}
                </p>
              </div>
            </div>
            <ExpressInterestButton target_user_id={p.user_id} size="lg" />
          </div>

          {/* Value prop first — Offers + Looking For */}
          {(p.offers?.length || p.looking_for?.length) > 0 && (
            <section className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="pro-detail-value-prop">
              <div className="rounded-2xl border border-[color:var(--brand-green)]/30 bg-[color:var(--brand-green)]/5 p-5">
                <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-green)]">
                  <Package className="h-3.5 w-3.5" /> What I offer
                </p>
                <div className="mt-3"><ChipList items={p.offers || []} limit={20} tone="green" /></div>
              </div>
              <div className="rounded-2xl border border-[color:var(--brand-orange)]/30 bg-[color:var(--brand-orange)]/5 p-5">
                <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-orange)]">
                  <Target className="h-3.5 w-3.5" /> What I'm looking for
                </p>
                <div className="mt-3"><ChipList items={p.looking_for || []} limit={20} tone="orange" /></div>
                {p.looking_for_details && (
                  <p className="mt-3 text-xs text-[color:var(--ink-soft)]">{p.looking_for_details}</p>
                )}
              </div>
            </section>
          )}

          {p.bio && (
            <section className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">About</p>
              <p className="mt-2 whitespace-pre-line text-[color:var(--ink)]">{p.bio}</p>
            </section>
          )}

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {(pi.expertise?.length || 0) > 0 && (
              <div className="rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Expertise</p>
                <div className="mt-3"><ChipList items={pi.expertise} limit={20} tone="soft" /></div>
              </div>
            )}
            {(pi.services?.length || 0) > 0 && (
              <div className="rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Services</p>
                <div className="mt-3"><ChipList items={pi.services} limit={20} tone="soft" /></div>
              </div>
            )}
          </div>

          {p.business && (
            <div className="mt-8 border-t border-[color:var(--hairline)] pt-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Business</p>
              <Link to={`/app/businesses/${p.business.id}`} data-testid="pro-business-link"
                className="mt-3 flex items-center gap-4 rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4 transition-colors hover:border-[color:var(--ink)]">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[color:var(--soft)]">
                  {p.business.logo ? <img src={p.business.logo} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-5 w-5 text-[color:var(--ink-soft)]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[color:var(--ink)]">{p.business.name}</p>
                  <p className="truncate text-xs text-[color:var(--ink-soft)]">{p.business.industry} · {[p.business.city, p.business.country].filter(Boolean).join(", ")}</p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-[color:var(--ink)]" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
